import { supabase } from '../config/supabase';
import { isFinancialRpcAbort, normalizeFinancialRpcError } from './financialRpcError';

export interface FinancialSetupOverview {
  services: { total: number; active: number };
  cashAccounts: { total: number; active: number };
  organizations: { total: number; active: number };
  payerLinks: { total: number; active: number };
  contracts: { total: number; draft: number; active: number };
  packageTemplates: { total: number; active: number; ready: number };
  settlementRules: { professionals: number; coveredProfessionals: number };
  absencePolicy: { hasActive: boolean; hasDraft: boolean };
}

export type FinancialCashAccountAdmin = {
  cashAccountId: string;
  accountName: string;
  accountType: 'bank' | 'cash' | 'wallet' | 'clearing';
  accountStatus: 'active' | 'inactive' | 'archived';
  openingBalance: number;
  hasMovements: boolean;
};

const countKeys: Array<[keyof FinancialSetupOverview, string[]]> = [
  ['services', ['total', 'active']], ['cashAccounts', ['total', 'active']],
  ['organizations', ['total', 'active']], ['payerLinks', ['total', 'active']],
  ['contracts', ['total', 'draft', 'active']], ['packageTemplates', ['total', 'active', 'ready']],
  ['settlementRules', ['professionals', 'coveredProfessionals']],
];

function isOverview(value: unknown): value is FinancialSetupOverview {
  if (!value || typeof value !== 'object') return false;
  const record = value as Record<string, unknown>;
  const countsValid = countKeys.every(([group, keys]) => {
    const item = record[group];
    return Boolean(item) && typeof item === 'object'
      && keys.every(key => {
        const count = (item as Record<string, unknown>)[key];
        return typeof count === 'number' && Number.isInteger(count) && count >= 0;
      });
  });
  const policy = record.absencePolicy as Record<string, unknown> | undefined;
  return countsValid && Boolean(policy)
    && typeof policy?.hasActive === 'boolean' && typeof policy.hasDraft === 'boolean';
}

export async function readFinancialSetupOverview(clientId: string, signal: AbortSignal): Promise<FinancialSetupOverview> {
  const response = await supabase.rpc('financial_setup_overview', { p_client_id: clientId }).abortSignal(signal);
  if (response.error && isFinancialRpcAbort({ ...response.error, status: response.status }, signal.aborted)) {
    throw new DOMException('Diagnóstico financeiro cancelado.', 'AbortError');
  }
  if (response.error) {
    throw new Error(normalizeFinancialRpcError({ ...response.error, status: response.status, statusText: response.statusText }).userMessage);
  }
  if (!isOverview(response.data)) throw new Error('O diagnóstico financeiro retornou um formato inesperado.');
  return response.data;
}

export async function readFinancialCashAccountsAdmin(clientId: string): Promise<FinancialCashAccountAdmin[]> {
  const { data, error } = await supabase.rpc('financial_a4_read_cash_accounts_admin', { p_client_id: clientId });
  if (error) throw new Error(error.message || 'Não foi possível carregar as contas financeiras.');
  if (!Array.isArray(data)) throw new Error('As contas financeiras retornaram um formato inesperado.');
  return data as FinancialCashAccountAdmin[];
}

export async function saveFinancialCashAccount(input: {
  clientId: string; cashAccountId?: string | null; name: string;
  type: FinancialCashAccountAdmin['accountType']; openingBalance: number;
  status: FinancialCashAccountAdmin['accountStatus'];
}): Promise<string> {
  const { data, error } = await supabase.rpc('financial_a4_save_cash_account', {
    p_client_id: input.clientId, p_cash_account_id: input.cashAccountId ?? null,
    p_name: input.name, p_type: input.type, p_opening_balance: input.openingBalance,
    p_status: input.status,
  });
  if (error) throw new Error(error.message || 'Não foi possível salvar a conta financeira.');
  if (typeof data !== 'string') throw new Error('A conta foi salva com retorno inválido.');
  return data;
}
